import { useSearchQueryParam } from '@piaas/piaas-ui-toolkit/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import Auth from '../../../../../api/auth';
import Table from '../../../../../components/Table';
import OrganizationBreadCrumb from '../../../../OrganizationDetails/OrganizationBreadCrumb';

import GitlabWikiHelpButton from '@/components/GitlabWikiHelpButton';
import useAppContext from '@/hooks/useAppContext';
import useTableApiCall from '@/hooks/useTableApiCall';
import { APPROVED_CALC_EXCLUSION_ROLES } from '@/rbac/rules';
import { useUserData } from '@/redux/selectors';
import type { Product } from '@/types/productTypes';
import type { TableRowData, TableField } from '@/types/tableTypes';

const productsTypeFilterMap: Record<string, string[]> = {
  caas: ['caasonopenstack', 'caasonbaremetal'],
  compute: ['evaluation', 'compute'],
  baremetal: ['baremetal'],
};

const usersGuideSlug = 'user-guide/organizations/products';

interface UserProductsProps {
  type: string;
}

const UserProducts = ({ type }: UserProductsProps): React.JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selectedOrganization } = useAppContext();
  const { organizationId, roles } = useUserData();
  const [rows, setRows] = useState<TableRowData[]>([]);

  const showApprovedStatus =
    !!roles && !roles.some(role => APPROVED_CALC_EXCLUSION_ROLES.includes(role));

  const transformProjectsToRows = useCallback(
    (data: Product[]) => {
      return data.map(
        ({ _id, name, description, marketplaceItem, project, details, approved }) => ({
          key: _id,
          onRowClick: () => navigate(`/products/${_id}`),
          data: [
            {
              field: 'name',
              value: name,
            },
            {
              field: 'description',
              value: description,
            },
            {
              field: 'type',
              value: marketplaceItem?.name,
              type: 'options',
            },
            {
              field: 'project',
              value: project.name,
            },
            {
              field: 'juicegroup',
              value: details.juiceGroup,
            },
            ...(type === 'compute' && showApprovedStatus
              ? [
                  {
                    field: 'approved',
                    value: approved ? 'True' : 'False',
                    type: 'options',
                  },
                ]
              : []),
          ],
        })
      );
    },
    [navigate, showApprovedStatus, type]
  );

  const handleRowsUpdate = useCallback(
    (data: Product[]) => {
      setRows(transformProjectsToRows(data) as TableRowData[]);
    },
    [transformProjectsToRows]
  );

  const memoizedDependency = useMemo(
    () => [selectedOrganization?._id ?? ''],
    [selectedOrganization?._id]
  );

  const [initialSearch] = useSearchQueryParam('search');

  const { fetchData, setCurrentPage, itemsPerPage, setItemsPerPage, ...tableData } =
    useTableApiCall(Auth.fetchV2Products as unknown as (...args: unknown[]) => Promise<unknown>, {
      isPaginated: true,
      setRows: handleRowsUpdate as (rows: unknown[]) => void,
      refetchDeps: memoizedDependency,
      enabled: !!(selectedOrganization?._id ?? organizationId),
      endpointParams: {
        types: productsTypeFilterMap[type],
        organizationId: selectedOrganization?._id ?? organizationId,
        ...(showApprovedStatus && {
          enrich: true,
        }),
      },
      searchColumns: ['name', 'description'],
      initialFilter: initialSearch,
      searchParamKey: 'search',
    });

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  const showOrganizationBreadCrumb = pathname.includes('/governance/organization');

  const fields = useMemo<TableField[]>(
    () => [
      { title: { id: 'name' }, id: 'name' },
      { title: { id: 'description' }, id: 'description' },
      { title: { id: 'type' }, id: 'type' },
      { title: { id: 'project' }, id: 'project' },
      { title: { id: 'juiceGroup' }, id: 'juicegroup' },
      ...(type === 'compute' && showApprovedStatus
        ? [{ title: { id: 'approved' }, id: 'approved' }]
        : []),
    ],
    [showApprovedStatus, type]
  );

  return (
    <>
      {showOrganizationBreadCrumb && <OrganizationBreadCrumb />}
      <Table
        header={intl.formatMessage({ id: 'products' })}
        fields={fields}
        rows={rows}
        pagination
        {...tableData}
        pageSize={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        headerActionSlot={{
          content: (
            <GitlabWikiHelpButton
              slug={usersGuideSlug}
              tooltipMessageId='usersGuide'
              tooltipDefaultMessage='Users guide'
              modalTitleMessageId='usersGuide'
              modalTitleDefaultMessage='Users guide'
              buttonAriaLabel='Open users guide'
              buttonSx={{ mt: 0 }}
            />
          ),
        }}
        setFilter={tableData.setFilter as (filter: unknown) => void}
        onPageChange={handlePageChange}
        error={tableData.error ? tableData.error.message : null}
        advancedSearch={false}
      />
    </>
  );
};

export default UserProducts;
